import type {
  CreateTrackInput,
  CursorPage,
  ImportTracksInput,
  ImportTracksResult,
  MusicExtraStats,
  MusicSource,
  MusicSummary,
  RequestWhitelistInput,
  Track,
  TrackUsage,
  TrackWhitelistRow,
  UpdateTrackInput,
  WhitelistStatus,
} from '@qlk/shared';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';

import { api, goiApi, type LoiApi } from '../../lib/api.js';

const GOC = '/api/v1/music';

export interface BoLocNhac {
  q?: string;
  sourceKind?: string;
  sourceId?: string;
  mood?: string;
  bpmMin?: number;
  bpmMax?: number;
  status?: string;
  contentIdStatus?: string;
}

export const KHOA = {
  danhSach: (loc: BoLocNhac) => ['music', 'tracks', loc] as const,
  tomTat: ['music', 'summary'] as const,
  thongKe: ['music', 'thong-ke'] as const,
  bai: (id: string) => ['music', 'track', id] as const,
  whitelist: (id: string) => ['music', 'whitelist', id] as const,
  suDung: (id: string) => ['music', 'usage', id] as const,
  suDungGanDay: ['music', 'usage', 'gan-day'] as const,
  suDungKenh: (channelId: string) => ['music', 'usage', 'kenh', channelId] as const,
  nguon: ['music', 'sources'] as const,
  goiY: (channelId: string, q: string) => ['music', 'suggest', channelId, q] as const,
};

function thamSo(loc: BoLocNhac, cursor?: string): string {
  const sp = new URLSearchParams();
  for (const [khoa, giaTri] of Object.entries(loc)) {
    if (giaTri !== undefined && giaTri !== '') sp.set(khoa, String(giaTri));
  }
  if (cursor) sp.set('cursor', cursor);
  const chuoi = sp.toString();
  return chuoi ? `?${chuoi}` : '';
}

type TrangBai = CursorPage<Track>;

export function useDanhSachBai(loc: BoLocNhac) {
  return useInfiniteQuery<
    TrangBai,
    LoiApi,
    InfiniteData<TrangBai>,
    ReturnType<typeof KHOA.danhSach>,
    string | undefined
  >({
    queryKey: KHOA.danhSach(loc),
    queryFn: ({ pageParam }) => api.get<TrangBai>(`${GOC}/tracks${thamSo(loc, pageParam)}`),
    initialPageParam: undefined,
    getNextPageParam: (trang) => trang.nextCursor ?? undefined,
  });
}

export function useTomTatNhac() {
  return useQuery<MusicSummary, LoiApi>({
    queryKey: KHOA.tomTat,
    queryFn: () => api.get<MusicSummary>(`${GOC}/summary`),
  });
}

export function useThongKeNhac() {
  return useQuery<MusicExtraStats, LoiApi>({
    queryKey: KHOA.thongKe,
    queryFn: () => api.get<MusicExtraStats>(`${GOC}/thong-ke`),
  });
}

export function useNguonNhac() {
  return useQuery<MusicSource[], LoiApi>({
    queryKey: KHOA.nguon,
    queryFn: () => api.get<MusicSource[]>(`${GOC}/nguon`),
  });
}

export function useBaiNhac(id: string | undefined) {
  return useQuery<Track, LoiApi>({
    queryKey: KHOA.bai(id ?? ''),
    queryFn: () => api.get<Track>(`${GOC}/tracks/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

export function useWhitelistCuaBai(id: string | undefined) {
  return useQuery<TrackWhitelistRow[], LoiApi>({
    queryKey: KHOA.whitelist(id ?? ''),
    queryFn: () => api.get<TrackWhitelistRow[]>(`${GOC}/tracks/${id ?? ''}/whitelist`),
    enabled: Boolean(id),
  });
}

export function useSuDungCuaBai(id: string | undefined) {
  return useQuery<TrackUsage[], LoiApi>({
    queryKey: KHOA.suDung(id ?? ''),
    queryFn: () => api.get<TrackUsage[]>(`${GOC}/tracks/${id ?? ''}/su-dung`),
    enabled: Boolean(id),
  });
}

export function useSuDungGanDay() {
  return useQuery<TrackUsage[], LoiApi>({
    queryKey: KHOA.suDungGanDay,
    queryFn: () => api.get<TrackUsage[]>(`${GOC}/su-dung-gan-day`),
  });
}

export function useSuDungCuaKenh(channelId: string | undefined) {
  return useQuery<TrackUsage[], LoiApi>({
    queryKey: KHOA.suDungKenh(channelId ?? ''),
    queryFn: () => api.get<TrackUsage[]>(`${GOC}/kenh/${channelId ?? ''}/su-dung`),
    enabled: Boolean(channelId),
  });
}

/** Gợi ý bài nhạc dùng được cho một kênh — `TrackPicker` dùng. */
export function useGoiYBai(channelId: string, q: string, moods: string[] = []) {
  return useQuery<Track[], LoiApi>({
    queryKey: KHOA.goiY(channelId, `${q}|${moods.join(',')}`),
    queryFn: () => {
      const sp = new URLSearchParams({ channelId, limit: '20' });
      if (moods.length > 0) sp.set('moods', moods.join(','));
      return api.get<Track[]>(`${GOC}/goi-y?${sp.toString()}`);
    },
    enabled: Boolean(channelId),
  });
}

/* ------------------------------- Mutation ------------------------------ */

function lamMoiDanhSach(qc: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['music', 'tracks'] }),
    qc.invalidateQueries({ queryKey: KHOA.tomTat }),
    qc.invalidateQueries({ queryKey: KHOA.thongKe }),
  ]);
}

export function useTaoBaiNhac() {
  const qc = useQueryClient();
  return useMutation<Track, LoiApi, CreateTrackInput>({
    mutationFn: (input) => api.post<Track>(`${GOC}/tracks`, input),
    onSuccess: () => lamMoiDanhSach(qc),
  });
}

export function useCapNhatBaiNhac(id: string) {
  const qc = useQueryClient();
  return useMutation<Track, LoiApi, UpdateTrackInput>({
    mutationFn: (input) =>
      goiApi<Track>(`${GOC}/tracks/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KHOA.bai(id) });
      await lamMoiDanhSach(qc);
    },
  });
}

export function useGuiYeuCauWhitelist() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, LoiApi, RequestWhitelistInput>({
    mutationFn: (input) => api.post<{ ok: true }>(`${GOC}/whitelist/yeu-cau`, input),
    onSuccess: async (_du, input) => {
      if (input.trackId) await qc.invalidateQueries({ queryKey: KHOA.whitelist(input.trackId) });
      await lamMoiDanhSach(qc);
    },
  });
}

export function useDatWhitelist(trackId: string) {
  const qc = useQueryClient();
  return useMutation<TrackWhitelistRow, LoiApi, { channelId: string; status: WhitelistStatus }>({
    mutationFn: ({ channelId, status }) =>
      goiApi<TrackWhitelistRow>(`${GOC}/whitelist/${trackId}/${channelId}`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KHOA.whitelist(trackId) });
      await lamMoiDanhSach(qc);
    },
  });
}

export function useNhapCsv() {
  const qc = useQueryClient();
  return useMutation<ImportTracksResult, LoiApi, ImportTracksInput>({
    mutationFn: (input) => api.post<ImportTracksResult>(`${GOC}/import`, input),
    onSuccess: () => lamMoiDanhSach(qc),
  });
}
